import { html, css, nothing, PropertyValues } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import {
  ltnToastServiceContext,
  publicLedgerServiceContext,
  nsSettingsServiceContext,
} from '../../../../../ns-services/contexts.js';

import { LtnToastService } from '../../../../../ltn-toast/ltn-toast-service.js';

import { NSSettingsService, SettingsObject } from '../../../../../ns-settings/ns-settings-service.js';

import { PublicLedgerService } from '../../../../../ns-services/ns-public-ledger-service.js';

import JobSchema from '../../../../../models/job-schema.js';
import NSJobComponent from '../../../job.js';

import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';
import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type WaRadioGroup from '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-env-picker.js';
import { LedgerNetwork, showLedgerNetworkPicker } from '../../../../../helpers/public-ledger-network.js';
import { prefersReducedMotion } from '../../../../../helpers/motion.js';
import { isInterruptedLambdaCall, lambdaErrorCode } from '../../../../../helpers/lambda.js';
import { createAccountFailureCopy, SETUP_KEY_ELSEWHERE_COPY } from '../../../../../helpers/public-ledger-copy.js';
import { createAccountStart } from '../../../../../helpers/public-ledger-key-check.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/public-ledger/account/create.json';
import { jobSchema } from '../../../job-schema.js';

enum JobStage {
  INFO,
  CREATION,
  SAVE_MNEMONIC,
  DID_NOT_CONFIRM_MNEMONIC_SAVE,
  // An account creation stopped part way (PUBLIC_LEDGER_SETUP_INCOMPLETE), is still held by another attempt, or was cut off:
  // "Try again" goes on with what's missing
  SETUP_INCOMPLETE,
}

@customElement(NSCreatePublicLedgerAccount.is)
export default class NSCreatePublicLedgerAccount extends NSJobComponent {
  static get is() {
    return 'ns-create-public-ledger-account';
  }

  @consume({ context: publicLedgerServiceContext })
  private __pLedgerService?: PublicLedgerService;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  private __focusedStage: JobStage = JobStage.INFO;

  @query('#recovery-question')
  private __recoveryQuestion?: WaRadioGroup;

  @query('#consent-form')
  private __consentForm?: HTMLElement;

  @query('#question-warning-section')
  private __questionWarningSection?: HTMLElement;

  @query('#consent-warning-section')
  private __consentWarningSection?: HTMLElement;

  @query('.stage-heading')
  private __stageHeading?: HTMLElement;

  @state()
  private __mnemonic?: string[];

  @state()
  private __jobStage: JobStage = JobStage.INFO;

  @state()
  private __message: string = 'Setting up your account';

  @state()
  private __hasBeenSaved: Boolean = false;

  @state()
  private __appMode?: string;

  // The company-level network choice, offered only to the first director outside production (showLedgerNetworkPicker)
  @state()
  private __showNetworkPicker: boolean = false;

  @state()
  private __network: LedgerNetwork = 'TESTNET';

  @state()
  private __failureText: string | null = null;

  static styles = [
    ...super.styles,
    css`
      ns-job-layout {
        --ns-job-layout-image-position: center;
      }

      .stage-heading {
        margin: 0;
        font-size: var(--wa-font-size-l);
        font-weight: 500;
      }

      .stage-heading:focus,
      .stage-heading:focus-visible {
        outline: none;
      }

      #recovery-question h3 {
        margin: 0;
      }

      .hint {
        margin: var(--wa-space-2xs) 0 var(--wa-space-m);
        color: var(--wa-color-text-quiet);
      }

      .warning {
        align-items: center;
        gap: var(--wa-space-s);
      }

      #question-warning-section,
      #consent-warning-section {
        display: none;
      }
      #question-warning-section.show,
      #consent-warning-section.show {
        display: flex;
      }

      /* The text and its copy and download buttons share a row, the buttons centred against the text */
      .phrase-intro {
        display: flex;
        align-items: center;
        gap: var(--wa-space-s);
        margin-block-end: var(--wa-space-s);
      }
      .phrase-intro p {
        flex: 1;
        margin: 0;
      }

      .phrase-tools {
        display: flex;
        flex: none;
        align-items: center;
        gap: var(--wa-space-xs);
      }

      /* Square, so the circular icon button isn't stretched into a tall pill. */
      .save-option-button svg {
        width: 15px;
        height: 15px;
      }

      .words-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
        gap: var(--wa-space-xs);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .words-grid li {
        display: flex;
        align-items: center;
        gap: var(--wa-space-2xs);
      }

      .words-grid .word-idx {
        flex: 0 0 3ch;
        text-align: end;
        color: var(--wa-color-text-quiet);
      }

      .words-grid .word {
        flex: 1;
        min-width: 0;
        padding: var(--wa-space-2xs) var(--wa-space-xs);
        border-radius: var(--wa-border-radius-pill);
        background: var(--wa-color-surface-default);
        color: var(--wa-color-text-normal);
        overflow-wrap: break-word;
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find router service');

    if (!this._directory) throw new Error('Unable to find dir service');

    if (!this._auth) throw new Error('Auth service is required!');

    if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');

    if (!this.__toast) throw new Error('Unable to find toast service');

    if (!this._job) throw new Error('Missing the job');
    if (!this._job.workflow.id) throw new Error('Missing job workflow id');

    if (!this.__settings) throw new Error('Unable to find settings service');

    // App settings
    const app = this.__settings.settings.app as SettingsObject;
    if (!app) throw new Error('Unable to find app settings');
    this.__appMode = app.mode as string;
    const company = this._directory.company;
    this.__showNetworkPicker = showLedgerNetworkPicker({
      appMode: this.__appMode,
      director: this._directory.me?.director,
      companyNetwork: company?.ledgerCred?.network,
      // The company account's key is a key list of its signatories, so the account id alone says it exists
      companyHasAccount: !!company?.ledgerCred?.accountId,
    });

    const creds = await this.__pLedgerService.getCreds();
    if (creds?.mnemonic) {
      this.__mnemonic = creds.mnemonic.split(' ');
    }

    const jobStageMetadataVal = await this.getMetadataValue('JOB_STAGE');
    if (jobStageMetadataVal === 'SAVE_MNEMONIC' && !this.__mnemonic) {
      this.__jobStage = JobStage.DID_NOT_CONFIRM_MNEMONIC_SAVE;
    }
    const hasBeenSavedMetadataVal = (await this.getMetadataValue('HAS_BEEN_SAVE')) as string;
    if (hasBeenSavedMetadataVal && hasBeenSavedMetadataVal.toUpperCase() === 'TRUE') {
      this.__hasBeenSaved = true;
    }

    const workflow = await this._db.getById('workflow', this._job.workflow.id);
    const metadata = workflow?.metadata?.find((m: { key: string; value: string }) => m.key === 'ADDED_TO_KEY_LIST');
    if (metadata && metadata.value === 'PUBLIC_LEDGER_ACCOUNT') {
      this.__message = "A request has been made to add you to the key list of your company's public ledger account";
    }
    if (metadata && metadata.value === 'PUBLIC_LEDGER_TOPIC') {
      this.__message = "A request has been made to add you to the key list of your company's public ledger topic";
    }
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('__jobStage') && this.__jobStage !== this.__focusedStage) {
      this.__focusedStage = this.__jobStage;
      this.__stageHeading?.focus();
    }
  }

  private async __createAccount() {
    if (!this._job || !this._job.workflow || !this._job.workflow.id) throw new Error('Missing job or job workflow ID');
    this.clearError();

    try {
      if (!this._db) throw new Error('Missing db service');
      if (!this._directory) throw new Error('Missing dir service');
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');
      if (!this._directory.me || !this._directory.me.id) throw new Error('Missing dir profile ID');
      const ledgerCred = this._directory.me.ledgerCred;
      // A set-up that stopped part way (or whose answer never arrived) may have made this person's account already, with the
      // key this device kept for it: trying again then goes on to what's missing. Made with a key this browser no longer
      // holds, it can only be finished from the browser that started it.
      const start = createAccountStart({
        profilePublicKey: ledgerCred.publicKey,
        profileHasAccountDetails: Boolean(
          ledgerCred.accountId || ledgerCred.url || ledgerCred.topicId || ledgerCred.verifiedAt,
        ),
        pendingPublicKey: this.__pLedgerService.pendingPublicKey(),
        devicePublicKey: this.__pLedgerService.publicKeyOnDevice(),
      });
      if (start === 'KEY_ELSEWHERE') throw new Error(SETUP_KEY_ELSEWHERE_COPY);
      if (start === 'HAS_ACCOUNT') throw new Error('Your device already holds public ledger account for your user');

      this.__questionWarningSection?.classList.remove('show');

      this.loading('Creating a public ledger account');
      // "Try again" after a set-up that stopped part way has no question form on screen; it was answered the first time
      const passed = this.__jobStage === JobStage.SETUP_INCOMPLETE || this.__checkCreationQuestionForm();
      if (!passed) {
        this.loaded();
        this.__questionWarningSection?.classList.add('show');
        this.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'end',
          inline: 'nearest',
        });
        return;
      }

      // The server refuses a network the platform has no credentials for yet, with a message saying so, before it creates anything
      // The job id: the server lets only the director this card is assigned to by the company's onboarding set the company up
      await this.__pLedgerService.addAccount(this.job.id, this.__showNetworkPicker ? this.__network : null);
      await this.updateMetadata('JOB_STAGE', 'SAVE_MNEMONIC');
      this.__jobStage = JobStage.SAVE_MNEMONIC;

      const creds = await this.__pLedgerService.getCreds();
      this.__mnemonic = creds?.mnemonic?.split(' ');
      this.__failureText = null;

      this.loaded();
    } catch (err) {
      this.loaded();
      const failure = createAccountFailureCopy(
        lambdaErrorCode(err),
        err instanceof Error ? err.message : null,
        isInterruptedLambdaCall(err),
      );
      if (failure.retry) {
        this.__failureText = failure.text;
        this.__jobStage = JobStage.SETUP_INCOMPLETE;
      } else {
        this.showError(failure.text ? new Error(failure.text) : err);
      }
      this.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  private __checkCreationQuestionForm() {
    if (!this.__recoveryQuestion) throw new Error('Missing required recovery question in __checkCreationQuestionForm');

    return this.__recoveryQuestion.value === 'permanently-lost';
  }

  private __checkConsentForm() {
    if (!this.__consentForm) throw new Error('Missing required consent form element in __confirmMnemonicSave');
    const checkboxes = this.__consentForm.querySelectorAll<WaCheckbox>('wa-checkbox');
    if (checkboxes.length < 1) throw new Error('Missing required checkboxes from the consent form');

    return Array.from(checkboxes).every((checkbox) => checkbox.checked);
  }

  private __backStage() {
    if (!this._job || !this._job.id) throw new Error('Missing required job');
    this.clearError();
    if (JobStage[this.__jobStage - 1]) {
      this.__jobStage = this.__jobStage - 1;
    }
  }

  private __forwardStage() {
    if (!this._job || !this._job.id) throw new Error('Missing required job');
    this.clearError();
    if (JobStage[this.__jobStage + 1]) {
      this.__jobStage = this.__jobStage + 1;
    }
  }

  private __confirmMnemonicSave() {
    if (!this._job || !this._job.id) throw new Error('Missing required job');
    this.clearError();

    if (!this.__consentWarningSection) throw new Error('Can not find warning section element');
    this.__consentWarningSection.classList.remove('show');

    try {
      const consent = this.__checkConsentForm();
      if (!consent) {
        this.__consentWarningSection.classList.add('show');
        this.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'end',
          inline: 'nearest',
        });
        return;
      }
      if (!this.__hasBeenSaved)
        throw new Error('Please make sure to either copy/save your recovery phrase somewhere safe or download it.');

      this.outcome('complete');
    } catch (err) {
      this.showError(err);
      this.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  private async __copyToClipBoard() {
    if (!this._job || !this._job.id) throw new Error('Missing required job');
    this.clearError();

    if (!this.__toast) throw new Error('Missing required toast service');
    if (!this.__mnemonic) throw new Error("Missing mnemonic to copy to user's clipboard");

    const recoveryPhrase = this.__mnemonic.join(' ');
    await navigator.clipboard.writeText(recoveryPhrase);
    this.__hasBeenSaved = true;
    await this.updateMetadata('HAS_BEEN_SAVE', 'true');

    this.__toast?.queueToast({
      label: 'Recovery phrase has been copied to your clipboard',
      timeout: 4000,
    });
  }

  private async __download() {
    if (!this._job || !this._job.id) throw new Error('Missing required job');
    this.clearError();

    if (!this.__toast) throw new Error('Missing required toast service');
    if (!this.__mnemonic) throw new Error("Missing mnemonic to copy to user's clipboard");

    const recoveryPhrase = this.__mnemonic.join(' ');
    const blob = new Blob([recoveryPhrase], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'nodeStream_public_ledger_account_recovery_phrase';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.__hasBeenSaved = true;
    await this.updateMetadata('HAS_BEEN_SAVE', 'true');

    this.__toast?.queueToast({
      label: 'Recovery phrase file has been downloaded to your device',
      timeout: 4000,
    });
  }

  private __renderInfoStage() {
    return html`
      <h2 slot="heading" class="stage-heading" tabindex="-1">${this.__message}</h2>
      <div class="job-panel">
        <h3>Smarter collaboration starts with trust.</h3>
        <p>
          Here you'll set up your public ledger account, which is your verified identity on nodeStream. A transparent,
          permanent record of the work you've completed, the workflows you've joined, and the collaborations you've been
          part of. It's your living archive, showing how you contribute and connect across the network.
        </p>
      </div>
      <div slot="aside" class="primary-info">
        <span class="info-icon" aria-hidden="true">i</span>
        <h3>Why it matters</h3>
        <p>
          In nodeStream, every action counts. By linking your activity to a verified ledger account, you create a
          reliable, auditable history of your contributions — visible to collaborators, clients, and partners. It's
          transparency that strengthens trust and proves value in every project.
        </p>
        <p class="note">
          Next, we'll generate your 24-word passphrase — your private key to access and recover your account. Keep it
          secure and never share it.
        </p>
      </div>
    `;
  }

  private __renderNetworkForm() {
    return html`
      <div class="job-panel">
        <p>
          This choice applies to your whole company: everyone's public ledger account uses it, and it can't be changed
          later.
        </p>
        <ns-env-picker
          service="Company public ledger"
          live-label="Live public ledger"
          sandbox-label="Test public ledger"
          live-value="MAINNET"
          sandbox-value="TESTNET"
          .value=${this.__network}
          @env-change=${(e: CustomEvent<{ value: string }>) =>
            (this.__network = e.detail.value === 'MAINNET' ? 'MAINNET' : 'TESTNET')}
        ></ns-env-picker>
      </div>
    `;
  }

  private __renderCreationStage() {
    return html`
      <h2 slot="heading" class="stage-heading" tabindex="-1">${this.__message}</h2>
      ${this.__showNetworkPicker ? this.__renderNetworkForm() : nothing}
      <div class="job-panel">
        <h3>Protect your access</h3>
        <p>
          There are no resets. Your 24-word recovery phrase is the only way to restore your nodeStream account. It's not
          stored on our servers, and even our team can't retrieve it.
        </p>
        <p>Before you continue, let's make sure you understand what that means.</p>
      </div>
      <div class="job-panel">
        <wa-radio-group id="recovery-question">
          <div slot="label">
            <h3>What happens if you lose your recovery phrase?</h3>
            <p class="hint">(Check the statement you believe is true)</p>
          </div>
          <wa-radio class="job-option" value="permanently-lost">
            I'll permanently lose access to my account, and there's no way to recover it.
          </wa-radio>
          <wa-radio class="job-option" value="reset-in-settings">I can reset it later in my account settings.</wa-radio>
          <wa-radio class="job-option" value="stored-by-nodestream">
            It's stored securely by nodeStream for emergencies.
          </wa-radio>
        </wa-radio-group>
        <p id="question-warning-section" class="note warning" role="alert">
          <ns-icon icon="warning" aria-hidden="true"></ns-icon>
          Incorrect. There's no reset or replacement — only your recovery phrase can unlock your account.
        </p>
      </div>
    `;
  }

  private __renderSaveMnemonicStage() {
    return html`
      <h2 slot="heading" class="stage-heading" tabindex="-1">Setting up your account</h2>
      <div class="job-panel">
        <h3>Here's your recovery phrase</h3>
        <div class="phrase-intro">
          <p>
            These 24 words are your private key to your nodeStream account. Keep them safe and offline, they're the only
            way to recover your access if it's ever lost.
          </p>
          <div id="save-options" class="phrase-tools">
            <wa-button
              type="button"
              variant="brand"
              appearance="outlined"
              class="icon-button circle save-option-button"
              @click="${() => this.__copyToClipBoard()}"
            >
              <svg
                class="clickable"
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 0 24 24"
                width="24px"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M0 0h24v24H0z" fill="none" />
                <path
                  d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                />
              </svg>
              <span class="visually-hidden">Copy recovery phrase to clipboard</span>
            </wa-button>
            <wa-button
              type="button"
              variant="brand"
              appearance="outlined"
              class="icon-button circle save-option-button"
              @click="${() => this.__download()}"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                enable-background="new 0 0 24 24"
                height="24px"
                viewBox="0 0 24 24"
                width="24px"
                fill="currentColor"
                aria-hidden="true"
                focusable="false"
              >
                <g><rect fill="none" height="24" width="24" /></g>
                <g><path d="M5,20h14v-2H5V20z M19,9h-4V3H9v6H5l7,7L19,9z" /></g>
              </svg>
              <span class="visually-hidden">Download recovery phrase as a file</span>
            </wa-button>
          </div>
        </div>
        <ol class="words-grid">
          ${this.__mnemonic
            ? repeat(
                this.__mnemonic,
                (word, idx) => html`
                  <li>
                    <span class="word-idx">${idx + 1}.</span>
                    <span class="word">${word}</span>
                  </li>
                `,
              )
            : undefined}
        </ol>
      </div>

      <div id="consent-form" class="job-panel">
        <fieldset class="fieldset">
          <legend class="visually-hidden">Confirm you've saved your recovery phrase</legend>
          <wa-checkbox class="job-option">I have written down and securely store these words</wa-checkbox>
          <wa-checkbox class="job-option">
            I understand that storing them digitally in an email, screenshot or text based software or cloud document is
            not a secure solution
          </wa-checkbox>
        </fieldset>
        <p id="consent-warning-section" class="note warning" role="alert">
          <ns-icon icon="warning" aria-hidden="true"></ns-icon>
          Please consent to the above.
        </p>
      </div>

      <div slot="aside" class="primary-info">
        <span class="info-icon" aria-hidden="true">i</span>
        <p>
          This is your unique secure 24 word pass phrase, it's very important that you store it in a secure location. If
          you lose this you will lose access to your account.
        </p>
      </div>
    `;
  }

  private __renderDidNotConfirmMnemonicSaveStage() {
    return html`
      <h2 slot="heading" class="stage-heading" tabindex="-1">
        You needed to confirm saving your public ledger account recovery phrase before refreshing.
      </h2>
      <div class="job-panel">
        <p>
          You need to contact nodeStream admin to create a new account for you in case you did not save your public
          ledger account recovery phrase.
        </p>
      </div>
    `;
  }

  private __renderSetUpIncompleteStage() {
    return html`
      <h2 slot="heading" class="stage-heading" tabindex="-1">Setting up your account</h2>
      <div class="job-panel" role="alert">
        <p>${this.__failureText}</p>
      </div>
    `;
  }

  render() {
    return html`
      <ns-job-layout image="/images/jobs/create-public-ledger.png">
        ${choose(this.__jobStage, [
          [JobStage.INFO, () => this.__renderInfoStage()],
          [JobStage.CREATION, () => this.__renderCreationStage()],
          [JobStage.SAVE_MNEMONIC, () => this.__renderSaveMnemonicStage()],
          [JobStage.DID_NOT_CONFIRM_MNEMONIC_SAVE, () => this.__renderDidNotConfirmMnemonicSaveStage()],
          [JobStage.SETUP_INCOMPLETE, () => this.__renderSetUpIncompleteStage()],
        ])}
      </ns-job-layout>
    `;
  }

  renderActions() {
    switch (this.__jobStage) {
      case JobStage.INFO:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.__forwardStage()}>Next</wa-button>
        `;
      case JobStage.CREATION:
        // Back at the far left of the bar, Next at the far right
        return html`
          <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__backStage()}>
            Back
          </wa-button>
          <wa-button variant="brand" type="button" @click=${() => this.__createAccount()}>Next</wa-button>
        `;
      case JobStage.SAVE_MNEMONIC:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.__confirmMnemonicSave()}>Complete Job</wa-button>
        `;
      case JobStage.SETUP_INCOMPLETE:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.__createAccount()}>Try again</wa-button>
        `;
      default:
        return nothing;
    }
  }
}
